import { TranslateService } from '@ngx-translate/core';
import { Action } from 'redux';
import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { concat, forkJoin, from, Observable, of } from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    map,
    mapTo,
    mergeMap,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { Util } from '../common/common.util';
import { Dependencies } from '../store';
import { ActionResult } from '../store.epic';
import { PeriodType } from '../widget/date-range.widget.model';

import {
    Category,
    checkPathLevels,
    Config,
    ConfigPath,
    ConfigPathLevel,
    ConfigShallow,
    ConfigView,
    ConfigViewMetric,
    ConfigViewPillar,
    createLevelPath,
    DataKey,
    DefinitionSummary,
    determineTrendPeriods,
    findLevel,
    flatten,
    flattenSupported,
    getUniqueQueueSuperGroupCombinations,
    HierarchyUserInfoItem,
    Override,
    padMissingMetricsWithMetrics,
    PillarsInGrid,
    RoleHierarchyItem,
    shouldGotoDashboard,
    TrendPeriodStart,
} from './config.insight.model';
import { ComponentType } from './enums/component-type.enum';
import { Column, flattenArrayWithObj } from './experimental.grid';
import { Exporter } from './experimental.grid-export';
import { ColumnContext, ColumnType, RowId } from './grid.insight.model';
import * as actions from './insight.action';
import { InsightEpicBridge } from './insight.epic.bridge';
import { InsightService } from './insight.service';
import { BaseState, MetricPath } from './insight.state';
import { MetricThreshold, PerformanceTrend } from './legend.insight.model';
import { SuperGroupQueue } from './models/super-group-queue.model';
import {
    OptionalColumn,
    OptionalColumnType,
    valueForColumn,
} from './optional-column.insight.model';
import { CacheDateItem } from './performance-data/cache-date.pd.model';
import {
    DataFilterOptions,
    FilterContext,
    generateDataFilterOptions,
    LevelDataMap,
    parseContextAttrs,
} from './performance-data/data-filter-options.pd.model';
import {
    CriteriaIdType,
    MetricData,
    MetricDataItem,
    NO_DATA_ID,
    PerformanceDataViewType,
    PerformanceDataViewTypeArgsMap,
    weightedScore,
} from './performance-data/metric.pd.model';
import { PerformanceDataService } from './performance-data/performance-data.service';

export const listDefinitionSummary =
    <T extends { components?: string[] }>(deps: Dependencies) =>
    (source: Observable<T>): Observable<ActionResult<T, Category[]>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<InsightService>(InsightService)
                    .listDefinitionSummary(action.components)
                    .pipe(map((result) => ({ action, result }))),
            ),
        );

export const retrieveOverride =
    <T>(deps: Dependencies) =>
    (source: Observable<any>): Observable<ActionResult<any, Override<T>>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<InsightService>(InsightService)
                    .retrieveOverride<T>()
                    .pipe(map((result) => ({ action, result }))),
            ),
        );

export const updateOverride =
    <TState, TAction extends { override: Override<TState> }>(
        deps: Dependencies,
    ) =>
    (source: Observable<TAction>): Observable<ActionResult<TAction, boolean>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<InsightService>(InsightService)
                    .updateOverride<TState>(action.override)
                    .pipe(map((result) => ({ action, result }))),
            ),
        );

export const retrieveConfigShallow =
    <T extends { identifier: string; component?: string }>(
        deps: Dependencies,
    ) =>
    (source: Observable<T>): Observable<ActionResult<T, ConfigShallow>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<InsightService>(InsightService)
                    .retrieveConfigShallow(action.identifier, action.component)
                    .pipe(map((result) => ({ action, result }))),
            ),
        );

export const retrieveConfig =
    <
        T extends {
            identifier: string;
            component?: string;
            criteriaId?: number;
            userId?: number;
            periodType: PeriodType;
            eventDate?: Date;
        },
    >(
        deps: Dependencies,
    ) =>
    (source: Observable<T>): Observable<ActionResult<T, Config>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<InsightService>(InsightService)
                    .retrieveConfig(
                        action.identifier,
                        action.component,
                        action.criteriaId,
                        action.userId,
                        action.periodType,
                        action.eventDate,
                    )
                    .pipe(map((result) => ({ action, result }))),
            ),
        );

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const listRoleHierarchy =
    <T>( // eslint-disable-line @typescript-eslint/no-unused-vars
        deps: Dependencies,
    ) =>
    (
        source: Observable<any>,
    ): Observable<ActionResult<any, RoleHierarchyItem[]>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<InsightService>(InsightService)
                    .listRoleHierarchy()
                    .pipe(map((result) => ({ action, result }))),
            ),
        );

export const listHierarchyUserInfo =
    <T extends { userIds: number[]; eventDate?: Date }>(deps: Dependencies) =>
    (
        source: Observable<T>,
    ): Observable<ActionResult<T, HierarchyUserInfoItem[]>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<InsightService>(InsightService)
                    .listHierarchyUserInfo(action.userIds, action.eventDate)
                    .pipe(map((result) => ({ action, result }))),
            ),
        );

export const listSubordinatesHierarchyUserInfo =
    <
        T extends {
            userId: number;
            eventDate?: Date;
            roleIds?: number[];
            lobIds?: number[];
            regionIds?: number[];
            areaIds?: number[];
            siteIds?: number[];
            peerGroupIds?: number[];
            superGroupIds?: number[];
            managerIds?: number[];
        },
    >(
        deps: Dependencies,
    ) =>
    (
        source: Observable<T>,
    ): Observable<ActionResult<T, HierarchyUserInfoItem[]>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<InsightService>(InsightService)
                    .listSubordinatesHierarchyUserInfo({
                        userId: action.userId,
                        eventDate: action.eventDate,
                        roleIds: action.roleIds,
                        lobIds: action.lobIds,
                        regionIds: action.regionIds,
                        areaIds: action.areaIds,
                        siteIds: action.siteIds,
                        peerGroupIds: action.peerGroupIds,
                        superGroupIds: action.superGroupIds,
                        managerIds: action.managerIds,
                    })
                    .pipe(map((result) => ({ action, result }))),
            ),
        );

export const listMetricThresholds =
    <T extends { metricIds: number[] }>(deps: Dependencies) =>
    (source: Observable<T>): Observable<ActionResult<T, MetricThreshold[]>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<InsightService>(InsightService)
                    .listMetricThresholds(action.metricIds)
                    .pipe(map((result) => ({ action, result }))),
            ),
        );

export const listTrendsArrows =
    <T>(deps: Dependencies) =>
    (source: Observable<T>): Observable<ActionResult<T, PerformanceTrend[]>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<InsightService>(InsightService)
                    .listTrendsArrows()
                    .pipe(map((result) => ({ action, result }))),
            ),
        );

export const listSuperGroupQueues =
    <T>(
        deps: Dependencies,
        eventDate: Date,
        queuesIds: number[],
        sgIds: number[],
        userIds: number[],
    ) =>
    (source: Observable<T>): Observable<ActionResult<T, SuperGroupQueue[]>> =>
        source.pipe(
            mergeMap(() =>
                deps.injector
                    .get<InsightService>(InsightService)
                    .listSuperGroupQueues(eventDate, queuesIds, sgIds, userIds),
            ),
            map((result) => ({ action: null, result })),
            catchError((error) => {
                console.error('Error calling listSuperGroupQueues:', error);
                return of({ action: null, result: [] });
            }),
        );

export const listCacheDates =
    <T extends { periodType: PeriodType; userId: number }>(
        deps: Dependencies,
    ) =>
    (source: Observable<T>): Observable<ActionResult<T, CacheDateItem[]>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<PerformanceDataService>(PerformanceDataService)
                    .listCacheDates(action.periodType, action.userId)
                    .pipe(
                        catchError(() => of([])),
                        map((result) => ({ action, result })),
                    ),
            ),
        );

export const listOptionalColumns =
    <T>(deps: Dependencies) =>
    (source: Observable<T>): Observable<ActionResult<T, OptionalColumn[]>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<PerformanceDataService>(PerformanceDataService)
                    .listOptionalColumns()
                    .pipe(
                        catchError(() => of([])),
                        map((result) => ({ action, result })),
                    ),
            ),
        );

export const listData =
    <T extends { opts: DataFilterOptions }>(deps: Dependencies) =>
    (source: Observable<T>): Observable<ActionResult<T, MetricData[]>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<PerformanceDataService>(PerformanceDataService)
                    .listDataToMetricData(action.opts)
                    .pipe(
                        catchError(() => of<MetricData[]>([])),
                        map((result) => {
                            if (result.length === 0) {
                                result.push({
                                    id: NO_DATA_ID,
                                    name: null,
                                    shortName: null,
                                    initials: null,
                                    dataId: null,
                                    metrics: [],
                                    displayNameCriteriaIdType: null,
                                    outputCriteriaIdType: null,
                                    outputCriteriaIdTypes: null,
                                });
                            }
                            return result;
                        }),
                        map((result) => ({ action, result })),
                    ),
            ),
        );

/// //////////////////////

// props in order as defined in state interface
export interface BaseChainGroup<T extends Action> {
    root: T;
    summary?: Category[];
    category?: Category;
    subCategory?: Category;
    definition?: DefinitionSummary;
    configShallow?: ConfigShallow;
    periodType?: PeriodType;
    cacheDates?: CacheDateItem[];
    eventDate?: Date;
    gotoDashboard?: boolean;
    initialDataFilterOpts?: DataFilterOptions;
    trendPeriods?: TrendPeriodStart[];
    config?: Config;
    configUserInfo?: HierarchyUserInfoItem;
    view?: ConfigView;
    viewContext?: FilterContext;
    defaultPath?: string;
    path?: ConfigPath;
    roleHierarchy?: RoleHierarchyItem[];
    cacheLangId?: number;
    optionalColumns?: OptionalColumn[];
    pillars?: ConfigViewPillar[];
    supportedColumns?: OptionalColumn[];
    columns?: OptionalColumn[];
    metrics?: MetricPath[];
    dataMetric?: MetricDataItem;
    metricThresholds?: MetricThreshold[];
    trendsArrows?: PerformanceTrend[];
}

function getDefault<T extends { isDefault: boolean }>(items: T[]): T {
    const defaults = items.filter((e) => e.isDefault);
    return defaults.length !== 0
        ? defaults[0]
        : items.length !== 0
        ? items[0]
        : null;
}

export function categorySelect<TGroup extends BaseChainGroup<any>>(
    action: actions.RootSelectLevelOptionAction,
    deps: Dependencies,
    group: TGroup,
): Observable<TGroup> {
    group.category = action.category;
    const summary = group.summary;
    // eslint-disable-next-line @typescript-eslint/prefer-includes
    if (!group.category || summary.indexOf(group.category) === -1) {
        group.category = getDefault(summary);
    }
    return of(group);
}

export function subCategorySelect<TGroup extends BaseChainGroup<any>>(
    action: actions.RootSelectLevelOptionAction,
    deps: Dependencies,
    group: TGroup,
): Observable<TGroup> {
    group.subCategory = action.subCategory;
    const subCategories = group.category.subCategories;
    // eslint-disable-next-line @typescript-eslint/prefer-includes
    if (!group.subCategory || subCategories.indexOf(group.subCategory) === -1) {
        group.subCategory = getDefault(subCategories);
    }
    return of(group);
}

export function definitionSelect<TGroup extends BaseChainGroup<any>>(
    action: actions.RootSelectLevelOptionAction,
    deps: Dependencies,
    group: TGroup,
): Observable<TGroup> {
    group.definition = action.definition;
    const definitions = (group.subCategory || group.subCategory).definitions;
    // eslint-disable-next-line @typescript-eslint/prefer-includes
    if (!group.definition || definitions.indexOf(group.definition) === -1) {
        group.definition = getDefault(definitions);
    }
    return of(group);
}

function periodTypeSelect<TAction extends Action>(
    action: actions.PeriodSelectPeriodTypeAction,
    deps: Dependencies,
    group: BaseChainGroup<TAction>,
    userId?: number,
    defaultWhenNotFound: boolean = false,
): Observable<BaseChainGroup<TAction>> {
    const config = group.config || group.configShallow;
    const periodTypes = config?.periods ?? [];
    group.periodType = action.periodType;
    if (group.periodType == null) {
        const pt = config?.defaultPeriod ?? PeriodType.MONTH;
        group.periodType =
            periodTypes.find((e) => e === pt) ?? PeriodType.MONTH;
    }
    // maybe better here than when switching presentationview (trend view)
    if (!periodTypes.includes(PeriodType.WEEK)) {
        periodTypes.push(PeriodType.WEEK);
    }
    if (!periodTypes.includes(PeriodType.MONTH)) {
        periodTypes.push(PeriodType.MONTH);
    }
    const obses = periodTypes.map((e) => {
        return of({ periodType: e, userId }).pipe(listCacheDates(deps));
    });
    return forkJoin(obses).pipe(
        tap((datesDates) => {
            group.cacheDates = datesDates.reduce(
                (p, c) => p.concat(c.result),
                [],
            );

            let cd: CacheDateItem;
            if (group.periodType != null && group.eventDate != null) {
                cd = group.cacheDates.find(
                    (e) =>
                        e.periodType === action.periodType &&
                        e.eventDate.getTime() === group.eventDate.getTime(),
                );
            }
            if (cd == null && group.periodType != null) {
                cd = group.cacheDates.find(
                    (e) => e.periodType === group.periodType,
                );
            }
            if (cd == null && defaultWhenNotFound) {
                // default again
                let pt = config?.defaultPeriod ?? PeriodType.MONTH;
                pt = periodTypes.find((e) => e === pt) ?? PeriodType.MONTH;
                cd = group.cacheDates.find((e) => e.periodType === pt);
                group.periodType = cd?.periodType;
            }
            group.eventDate = cd?.eventDate;

            group.gotoDashboard = shouldGotoDashboard(
                config.dashboardDateRanges,
                group.eventDate,
            );
            group.trendPeriods = determineTrendPeriods(
                group.eventDate,
                group.cacheDates,
            );
        }),
        mapTo(group),
    );
}

function configRetrieve<TGroup extends BaseChainGroup<any>>(
    action: any,
    deps: Dependencies,
    group: TGroup,
): Observable<TGroup> {
    group.initialDataFilterOpts = {
        viewType: undefined,
        displayNameCriteriaIdType: undefined,
        outputCriteriaIdType: undefined,
        outputCriteriaIdTypes: undefined,
        periodType: undefined,
        date: undefined,
    };
    let criteriaId = null;
    const config = group.config || group.configShallow;
    if (config.initialCriteriaIdTypeArg) {
        const k = config.initialCriteriaIdTypeArg.toLowerCase();
        if (k === '_userid_') {
            criteriaId = deps.injector.get<AuthService>(AuthService).user.id;
        }
        if (criteriaId == null) {
            criteriaId = parseInt(k);
        }
        if (criteriaId != null) {
            if (config.initialCriteriaIdType === CriteriaIdType.LOB_ID) {
                group.initialDataFilterOpts.lobIdList = [criteriaId];
            }
            if (config.initialCriteriaIdType === CriteriaIdType.REGION_ID) {
                group.initialDataFilterOpts.regionIdList = [criteriaId];
            }
            if (config.initialCriteriaIdType === CriteriaIdType.AREA_ID) {
                group.initialDataFilterOpts.areaIdList = [criteriaId];
            }
            if (config.initialCriteriaIdType === CriteriaIdType.SITE_ID) {
                group.initialDataFilterOpts.siteIdList = [criteriaId];
            }
            if (config.initialCriteriaIdType === CriteriaIdType.PEER_GROUP_ID) {
                group.initialDataFilterOpts.peerGroupIdList = [criteriaId];
            }
            if (
                config.initialCriteriaIdType === CriteriaIdType.SUPER_GROUP_ID
            ) {
                group.initialDataFilterOpts.superGroupIdList = [criteriaId];
            }
            if (config.initialCriteriaIdType === CriteriaIdType.QUEUE_ID) {
                group.initialDataFilterOpts.queueIdList = [criteriaId];
            }
        }
    }
    // TODO remove. fallback for hi
    if (criteriaId == null && group.root.component === 'hi') {
        criteriaId = group.root.userId;
    }
    // TODO revise. have onLanguageSelect not update user.languageId
    // const langId = deps.injector.get<AuthService>(AuthService).user.languageId;
    const langId = parseInt(
        deps.injector.get<TranslateService>(TranslateService).currentLang,
    );
    return forkJoin(
        of({
            ...group.root,
            criteriaId,
            periodType: group.periodType,
            eventDate: group.eventDate,
        }).pipe(
            retrieveConfig(deps),
            // eslint-disable-next-line no-return-assign
            tap((ret) => (group.config = ret.result)),
        ),
        !group.roleHierarchy
            ? of(null).pipe(
                  listRoleHierarchy(deps),
                  // eslint-disable-next-line no-return-assign
                  tap((ret) => (group.roleHierarchy = ret.result)),
              )
            : of(group),
        group.cacheLangId !== langId || !group.optionalColumns
            ? of(null).pipe(
                  listOptionalColumns(deps),
                  tap((ret) => {
                      group.cacheLangId = langId;
                      group.optionalColumns = ret.result;
                  }),
              )
            : of(group),
        group.root.userId == null
            ? of(null)
            : of({
                  userIds: [group.root.userId],
                  eventDate: group.eventDate,
              }).pipe(
                  listHierarchyUserInfo(deps),
                  // eslint-disable-next-line no-return-assign
                  tap(
                      (ret) =>
                          (group.configUserInfo =
                              ret.result &&
                              ret.result.length !== 0 &&
                              ret.result[0]),
                  ),
                  map(() => group),
              ),
    ).pipe(mapTo(group));
}

export function viewSelect<TGroup extends BaseChainGroup<any>>(
    action: { view: ConfigView },
    deps: Dependencies,
    group: TGroup,
): Observable<TGroup> {
    group.view = action.view;
    const views = group.config.views;
    // eslint-disable-next-line @typescript-eslint/prefer-includes
    if (!group.view || views.indexOf(group.view) === -1) {
        if (views.length !== 0) {
            group.view = views[0];
        }
    }
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    group.viewContext = parseContextAttrs(group.view && group.view.attributes);

    if (group.initialDataFilterOpts) {
        // initialCriteriaIdTypeArg (applied in configRetrieve if not null) takes precedence
        // overridden by PERFORMANCE_INSIGHT__LEVEL_OPTION__SELECT with configUserInfo if !!lockToUserProfile
        // overridden by PERFORMANCE_INSIGHT__LEVEL_OPTION__SELECT if a QuickSwitchItem is found
        group.initialDataFilterOpts.lobIdList =
            group.initialDataFilterOpts.lobIdList ||
            group.viewContext.defaultLobId == null
                ? group.initialDataFilterOpts.lobIdList
                : [group.viewContext.defaultLobId];
        group.initialDataFilterOpts.regionIdList =
            group.initialDataFilterOpts.regionIdList ||
            group.viewContext.defaultRegionId == null
                ? group.initialDataFilterOpts.regionIdList
                : [group.viewContext.defaultRegionId];
        group.initialDataFilterOpts.areaIdList =
            group.initialDataFilterOpts.areaIdList ||
            group.viewContext.defaultAreaId == null
                ? group.initialDataFilterOpts.areaIdList
                : [group.viewContext.defaultAreaId];
        group.initialDataFilterOpts.siteIdList =
            group.initialDataFilterOpts.siteIdList ||
            group.viewContext.defaultSiteId == null
                ? group.initialDataFilterOpts.siteIdList
                : [group.viewContext.defaultSiteId];
        group.initialDataFilterOpts.peerGroupIdList =
            group.initialDataFilterOpts.peerGroupIdList ||
            group.viewContext.defaultPeerGroupId == null
                ? group.initialDataFilterOpts.peerGroupIdList
                : [group.viewContext.defaultPeerGroupId];
        group.initialDataFilterOpts.superGroupIdList =
            group.initialDataFilterOpts.superGroupIdList ||
            group.viewContext.defaultSuperGroupId == null
                ? group.initialDataFilterOpts.superGroupIdList
                : [group.viewContext.defaultSuperGroupId];
        group.initialDataFilterOpts.queueIdList =
            group.initialDataFilterOpts.queueIdList ||
            group.viewContext.defaultQueueId == null
                ? group.initialDataFilterOpts.queueIdList
                : [group.viewContext.defaultQueueId];
    }

    return of(group);
}

export function pathSelect<TGroup extends BaseChainGroup<any>>(
    action: { path: ConfigPath },
    deps: Dependencies,
    group: TGroup,
): Observable<TGroup> {
    group.path = action.path;
    const paths = group.config.paths;
    // eslint-disable-next-line @typescript-eslint/prefer-includes
    if (!group.path || paths.indexOf(group.path) === -1) {
        if (paths.length !== 0) {
            group.path = paths[0];
        }
    }
    return of(group);
}

function dataForLevel<TAction extends Action>(
    deps: Dependencies,
    group: BaseChainGroup<TAction>,
    opts: DataFilterOptions,
    parentLevelsDataMap: LevelDataMap[], // modifies original
    data: DataKey[],
    depth: number = 0,
    noSingleRowAutoExpand: boolean = true,
): Observable<ActionResult<any, MetricData[]>> {
    const nextOpts = generateDataFilterOptions(opts, parentLevelsDataMap);
    return of({ opts: nextOpts }).pipe(
        concatMap((act) => {
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            if (
                act.opts.inputCriteriaIdTypes &&
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                act.opts.inputCriteriaIdTypes.indexOf(
                    CriteriaIdType.HIERARCHY,
                ) !== -1 &&
                act.opts.inputCriteriaIdTypeArgs
            ) {
                const typeId = `${CriteriaIdType.HIERARCHY}|`;
                const args = act.opts.inputCriteriaIdTypeArgs.filter((e) =>
                    e.startsWith(typeId),
                );
                const params = {} as any;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                args &&
                    args.forEach((e) => {
                        const parts = e
                            .substring(e.indexOf('|') + 1)
                            .split('|');
                        parts.forEach((ee) => {
                            try {
                                const kv = ee.toLowerCase().split(':');
                                if (kv[0] === 'userid') {
                                    if (kv[1] === '_managerid_') {
                                        params.userId =
                                            act.opts.managerIdList &&
                                            act.opts.managerIdList.length !==
                                                0 &&
                                            act.opts.managerIdList[0];
                                    }
                                }
                                if (kv[0] === 'roleids') {
                                    params.roleIds = kv[1]
                                        .split(',')
                                        .map((eee) => parseInt(eee));
                                }
                            } catch (err) {
                                console.error(err);
                            }
                        });
                    });
                if (params.userId != null) {
                    return of(params).pipe(
                        listSubordinatesHierarchyUserInfo(deps),
                        tap((ret) => {
                            act.opts.managerIdList = null;
                            act.opts.userIdList = [
                                ...(opts.userIdList || []),
                                ...ret.result.map((e) => e.userId),
                            ];
                        }),
                        mapTo(act),
                    );
                }
            }
            return of(act);
        }),
        listData(deps),
        tap((ret) => {
            if (!ret.result) return;
            const metrics = group.config.metrics; // TODO pad metrics at api level
            ret.result.forEach((e) => {
                e.metrics = padMissingMetricsWithMetrics(e.metrics, metrics);
                e.metrics.forEach((ee) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    const otherMetric =
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        metrics &&
                        metrics.find((eee) => eee.metricId === ee.id);
                    if (otherMetric) {
                        ee.name = otherMetric.name || ee.name;
                        ee.shortName = otherMetric.shortName || ee.shortName;
                        ee.initials = otherMetric.initials || ee.initials;
                        ee.weight = otherMetric.weight;
                        ee.weightedScore = weightedScore(
                            ee.actual,
                            ee.target,
                            ee.weight,
                        );
                    }
                });
            });

            const level = findLevel(group.path.levels, opts.identifier);
            if (level && (level.name || level.shortName || level.initials)) {
                ret.result.forEach((e) => {
                    e.name = level.name;
                    e.shortName = level.shortName;
                    e.initials = level.initials;
                });
            }
        }),
        tap((ret) => {
            data.push({
                levelId: opts.identifier,
                levelIdPath:
                    group.path &&
                    createLevelPath(group.path.levels, opts.identifier),
                depth,
                data: ret.result,
            });
        }),
        switchMap((ret) => {
            if (group.path) {
                let toExpand: MetricData[];
                if (
                    group.viewContext &&
                    group.viewContext.autoExpand.length !== 0
                ) {
                    toExpand = ret.result.filter((e) => {
                        if (e.id === NO_DATA_ID) return false;
                        return group.viewContext.autoExpand.some((ee) => {
                            return (
                                opts.identifier.startsWith(ee.levelId) &&
                                (ee.primaryId === -999 ||
                                    e.metrics.some(
                                        (eee) => eee.primaryId === ee.primaryId,
                                    )) &&
                                (ee.secondaryId === -999 ||
                                    e.metrics.some(
                                        (eee) =>
                                            eee.secondaryId === ee.secondaryId,
                                    )) &&
                                (ee.tertiaryId === -999 ||
                                    e.metrics.some(
                                        (eee) =>
                                            eee.tertiaryId === ee.tertiaryId,
                                    ))
                            );
                        });
                    });
                }
                if (
                    !noSingleRowAutoExpand &&
                    (!toExpand || toExpand.length === 0) &&
                    ret.result.length === 1 &&
                    ret.result[0].id !== NO_DATA_ID
                ) {
                    toExpand = [ret.result[0]];
                }
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                data.slice(-1)[0].autoExpand =
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    toExpand && toExpand.map((e) => e.id);
            }
            return of(ret);
        }),
        // switchMap(ret => {
        //     if (group.path) {
        //         let toExpand: Array<MetricData>;
        //         if (group.viewContext && group.viewContext.autoExpand.length !== 0) {
        //             toExpand = ret.result.filter(e => {
        //                 if (e.id === NO_DATA_ID) return false;
        //                 return group.viewContext.autoExpand.some(ee => {
        //                     return opts.identifier.startsWith(ee.levelId) &&
        //                         (ee.primaryId === -999 || e.metrics.some(eee => eee.id === ee.primaryId)) &&
        //                         (ee.secondaryId === -999 || e.metrics.some(eee => eee.id === ee.secondaryId));
        //                 });
        //             });
        //         } else {
        //             if (!noSingleRowAutoExpand && ret.result.length === 1 && ret.result[0].id !== NO_DATA_ID) {
        //                 toExpand = [ret.result[0]];
        //             }
        //         }
        //         if (toExpand && toExpand.length !== 0) {
        //             const obss = toExpand.map(e => {
        //                 const nextLevelsDataMap = (parentLevelsDataMap || []);
        //                 nextLevelsDataMap.splice(0, 0, {
        //                     identifier: opts.identifier,
        //                     performanceDataViewType: opts.viewType,
        //                     displayNameCriteriaIdType: opts.displayNameCriteriaIdType,
        //                     outputCriteriaType: opts.outputCriteriaIdType,
        //                     dataId: e.dataId
        //                 });
        //                 return dataForLevels(deps, group, opts.identifier, {
        //                     identifier: null,
        //                     viewType: null,
        //                     displayNameCriteriaIdType: null,
        //                     outputCriteriaIdType: null,
        //                     inputCriteriaIdTypes: null,
        //                     periodType: opts.periodType,
        //                     date: opts.date,
        //                     startDate: opts.startDate,
        //                     metricIdList: opts.metricIdList
        //                 }, nextLevelsDataMap, data, ++depth);
        //             });
        //             return forkJoin(...obss)
        //                 .pipe(map(() => ret));
        //         }
        //     }
        //     return of(ret);
        // })
    );
}

function getSuperGroupQueues<TAction extends Action>(
    deps: Dependencies,
    group: BaseChainGroup<TAction>,
    identifier: string,
    opts: DataFilterOptions,
    data: DataKey[],
    hierarchyUserInfoItems?: HierarchyUserInfoItem[],
    component?: ComponentType,
): Observable<ActionResult<any, DataKey[]>> {
    const levels = !identifier
        ? group.path.levels
        : findLevel(group.path.levels, identifier).levels;

    const isQueueIsSecondLast =
        levels[0]?.levels?.[0]?.performanceDataViewType ===
        PerformanceDataViewType.USER_QUEUE;
    const isSGQueueIsSecondLast =
        levels[0]?.levels?.[0]?.performanceDataViewType ===
        PerformanceDataViewType.SUPER_GROUP_QUEUE;
    const userIds = hierarchyUserInfoItems?.map((item) => item.userId) ?? [];
    const ids = isSGQueueIsSecondLast
        ? data?.[0]?.data?.map((item) => item.id)
        : [];
    const superGroupsIds =
        levels[0]?.performanceDataViewType ===
        PerformanceDataViewType.SUPER_GROUP
            ? ids
            : [];
    const queuesIds =
        levels[0]?.performanceDataViewType === PerformanceDataViewType.QUEUE
            ? ids
            : [];

    if (
        opts.date == null ||
        (component === ComponentType.HI &&
            (!isQueueIsSecondLast || userIds.length === 0)) ||
        (component === ComponentType.PI &&
            (!isSGQueueIsSecondLast || ids.length === 0))
    ) {
        return of({ action: null, result: data });
    }

    return listSuperGroupQueues(
        deps,
        opts.date,
        queuesIds,
        superGroupsIds,
        userIds,
    )(of(null)).pipe(
        map((res) => {
            const updatedData = data.map((d) => {
                d.queueLookupSuperGroup = getUniqueQueueSuperGroupCombinations(
                    res.result,
                );
                return d;
            });

            return { action: null, result: updatedData };
        }),
        catchError((error) => {
            console.error('Error fetching super group queues:', error);
            return of({ action: null, result: [] });
        }),
    );
}

function dataForLevels<TAction extends Action>(
    deps: Dependencies,
    group: BaseChainGroup<TAction>,
    identifier: string,
    opts: DataFilterOptions,
    levelsDataMap: LevelDataMap[],
    data: DataKey[],
    depth: number = 0,
    withPeerGroup?: boolean,
    dataPg?: DataKey[],
): Observable<ActionResult<any, DataKey[]>> {
    const levels = !identifier
        ? group.path.levels
        : findLevel(group.path.levels, identifier).levels;
    const noSingleAutoExpand = !(
        levels === group.path.levels && levels.length === 1
    );
    return forkJoin<ActionResult<any, MetricData[]>>(
        ...levels.map((e) => {
            const nextOpts: DataFilterOptions = {
                ...opts,
                identifier: e.identifier,
                viewType: e.performanceDataViewType,
                displayNameCriteriaIdType: e.displayNameCriteriaIdType,
                outputCriteriaIdType: e.outputCriteriaIdType,
                outputCriteriaIdTypes: e.outputCriteriaIdTypes
                    ? [...e.outputCriteriaIdTypes]
                    : null,
                inputCriteriaIdTypes: e.inputCriteriaIdTypes,
                inputCriteriaIdTypeArgs: e.inputCriteriaIdTypeArgs,
            };
            const nextLevelsDataMap = levelsDataMap.filter(
                (ee) => ee.identifier === e.identifier,
            );
            return dataForLevel(
                deps,
                group,
                nextOpts,
                nextLevelsDataMap,
                data,
                depth,
                noSingleAutoExpand,
            ).pipe(
                concatMap((ret) =>
                    !withPeerGroup
                        ? of(ret)
                        : dataForLevelPeerGroup(
                              deps,
                              group,
                              nextOpts,
                              nextLevelsDataMap,
                              data,
                              dataPg,
                              depth,
                              noSingleAutoExpand,
                          ).pipe(mapTo(ret)),
                ),
            );
        }),
    ).pipe(map(() => ({ action: null, result: data })));
}

function userIdsFromData(data: DataKey[]): number[] {
    if (!data) return null;
    return data
        .filter((e) => e.data)
        .reduce<MetricData[]>(
            (p, c) =>
                p.concat(
                    c.data.filter(
                        (e) =>
                            e.displayNameCriteriaIdType ===
                                CriteriaIdType.USER_ID ||
                            e.displayNameCriteriaIdType ===
                                CriteriaIdType.TEAM_ID,
                    ),
                ),
            [],
        )
        .map((e) => e.id);
}

function dataForLevelPeerGroup<TAction extends Action>(
    deps: Dependencies,
    group: BaseChainGroup<TAction>,
    opts: DataFilterOptions,
    levelsDataMap: LevelDataMap[],
    data: DataKey[],
    dataPg: DataKey[],
    depth: number = 0,
    noSingleRowAutoExpand: boolean = true,
): Observable<ActionResult<any, MetricData[]>> {
    const vt = PerformanceDataViewType.PEER_GROUP;
    const ps = PerformanceDataViewTypeArgsMap[vt];
    const userIds = userIdsFromData(data);

    return dataForLevel(
        deps,
        group,
        {
            identifier: opts.identifier,
            viewType: vt,
            displayNameCriteriaIdType: ps.primary,
            outputCriteriaIdType: ps.primary,
            outputCriteriaIdTypes: [ps.primary],
            inputCriteriaIdTypes: [CriteriaIdType.USER_ID],
            periodType: opts.periodType,
            date: opts.date,
            startDate: opts.startDate,
            roleId: opts.roleId,
            userIdList: userIds,
            metricIdList: opts.metricIdList,
        },
        levelsDataMap,
        dataPg,
        depth,
        noSingleRowAutoExpand,
    );
}

/// //////////////////////

export const pluck = <T = any>(obj: any, path: string): T => {
    const arrPath = path.split('.');
    return arrPath.reduce((p, c) => {
        if (!p) return p;
        return p[c];
    }, obj);
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const rootListDefinitionSummary = (
    action: ActionsObservable<actions.RootListDefinitionSummaryAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT__ROOT__LIST_DEFINITION_SUMMARY),
        switchMap((act) => {
            return of<BaseChainGroup<actions.RootListDefinitionSummaryAction>>({
                root: act,
            }).pipe(
                mergeMap((group) =>
                    of(group.root).pipe(
                        listDefinitionSummary(deps),
                        // eslint-disable-next-line no-return-assign
                        tap((ret) => (group.summary = ret.result)),
                        mapTo(group),
                    ),
                ),
                mergeMap((group) =>
                    categorySelect(
                        actions.rootSelectLevelOption(),
                        deps,
                        group,
                    ),
                ),
                mergeMap((group) =>
                    subCategorySelect(
                        actions.rootSelectLevelOption(),
                        deps,
                        group,
                    ),
                ),
                mergeMap((group) =>
                    definitionSelect(
                        actions.rootSelectLevelOption(),
                        deps,
                        group,
                    ),
                ),
                map((group) =>
                    actions.rootListDefinitionSummaryResult(
                        group.root.components,
                        group.summary,
                        group.category,
                        group.subCategory,
                        group.definition,
                    ),
                ),
                catchError(() => of(actions.rootListDefinitionSummaryResult())),
                takeUntil(
                    action.pipe(
                        ofType(
                            actions.ActionType
                                .INSIGHT__ROOT__LIST_DEFINITION_SUMMARY_CANCEL,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const rootSelectLevelOption = (
    action: ActionsObservable<actions.RootSelectLevelOptionAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT__ROOT__SELECT_LEVEL_OPTION),
        switchMap((act) => {
            const rootState = pluck<BaseState>(state.value, 'insight');

            const realAct = actions.rootSelectLevelOption(
                act.category || rootState.activeCategory,
                act.subCategory || rootState.activeSubCategory,
                act.definition || rootState.activeDefinition,
            );

            return of<BaseChainGroup<actions.RootSelectLevelOptionAction>>({
                root: act,
                summary: rootState.summary,
            }).pipe(
                mergeMap((group) => categorySelect(realAct, deps, group)),
                mergeMap((group) => subCategorySelect(realAct, deps, group)),
                mergeMap((group) => definitionSelect(realAct, deps, group)),
                map((group) =>
                    actions.rootSelectLevelOptionResult(
                        group.category,
                        group.subCategory,
                        group.definition,
                    ),
                ),
                catchError(() => of(actions.rootSelectLevelOptionResult())),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const rootRetrieveConfig = (
    action: ActionsObservable<actions.RootRetrieveConfigAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT__ROOT__RETRIEVE_CONFIG),
        switchMap((act) => {
            const rootState = pluck<BaseState>(state.value, 'insight');
            const userId = act.component === 'hi' ? act.userId : null;

            return of<BaseChainGroup<actions.RootRetrieveConfigAction>>({
                root: act,
                periodType: rootState.period.periodType,
                cacheDates: rootState.period.cacheDates,
                eventDate: rootState.period.eventDate,
                roleHierarchy: rootState.roleHierarchy,
                cacheLangId: rootState.cacheLangId,
                optionalColumns: rootState.optionalColumns,
            }).pipe(
                mergeMap((group) =>
                    of(group.root).pipe(
                        retrieveConfigShallow(deps),
                        // eslint-disable-next-line no-return-assign
                        tap((ret) => (group.configShallow = ret.result)),
                        mapTo(group),
                    ),
                ),
                mergeMap((group) =>
                    periodTypeSelect<actions.RootRetrieveConfigAction>(
                        actions.periodSelectPeriodType(
                            !act.persistPeriod ? null : group.periodType,
                        ),
                        deps,
                        group,
                        userId,
                        true,
                    ),
                ),
                mergeMap((group) => configRetrieve(null, deps, group)),
                tap((group) => {
                    if (
                        !group.config.views ||
                        !group.config.views.length ||
                        !group.config.paths ||
                        !group.config.paths.length
                    ) {
                        throw new Error('no views/paths. nothing to show');
                    }
                }),
                mergeMap((group) => viewSelect({ view: null }, deps, group)),
                mergeMap((group) =>
                    pathSelect(actions.rootSelectPath(null), deps, group),
                ),
                map((group) =>
                    actions.rootRetrieveConfigResult(
                        group.root.identifier,
                        group.root.component,
                        group.root.userId,
                        group.periodType,
                        group.cacheDates,
                        group.eventDate,
                        group.gotoDashboard,
                        group.initialDataFilterOpts,
                        group.trendPeriods,
                        group.config,
                        group.configUserInfo,
                        group.view,
                        group.viewContext,
                        group.path,
                        group.roleHierarchy,
                        group.cacheLangId,
                        group.optionalColumns,
                    ),
                ),
                catchError(() => of(actions.rootRetrieveConfigResult())),
                takeUntil(
                    action.pipe(
                        ofType(
                            actions.ActionType
                                .INSIGHT__ROOT__RETRIEVE_CONFIG_CANCEL,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const rootReloadConfig = (
    action: ActionsObservable<actions.RootReloadConfigAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT__ROOT__RELOAD_CONFIG),
        switchMap((act) => {
            const rootState = pluck<BaseState>(state.value, 'insight');

            return of<BaseChainGroup<actions.RootReloadConfigAction>>({
                root: act,
                periodType: rootState.period.periodType,
                eventDate: rootState.period.eventDate,
                config: rootState.configs[rootState.activeConfig],
                cacheLangId: rootState.cacheLangId,
                optionalColumns: rootState.optionalColumns,
            }).pipe(
                mergeMap((group) => configRetrieve(null, deps, group)),
                mergeMap((group) => viewSelect({ view: null }, deps, group)),
                mergeMap((group) =>
                    pathSelect(actions.rootSelectPath(null), deps, group),
                ),
                map((group) =>
                    actions.rootReloadConfigResult(
                        group.root.identifier,
                        group.root.component,
                        group.root.userId,
                        group.config,
                        group.configUserInfo,
                        group.view,
                        group.viewContext,
                        group.path,
                        group.initialDataFilterOpts,
                        group.cacheLangId,
                        group.optionalColumns,
                    ),
                ),
                catchError(() => of(actions.rootReloadConfigResult())),
                takeUntil(
                    action.pipe(
                        ofType(
                            actions.ActionType
                                .INSIGHT__ROOT__RELOAD_CONFIG_CANCEL,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootSelectVisualItem = (
    action: ActionsObservable<actions.RootSelectVisualItemAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT__ROOT__SELECT_VISUAL_ITEM),
        switchMap((act) => {
            const rootState = pluck<BaseState>(state.value, 'insight');

            return of<BaseChainGroup<actions.RootSelectVisualItemAction>>({
                root: act,
                periodType: rootState.period.periodType,
                eventDate: rootState.period.eventDate,
                config: rootState.configs[rootState.activeConfig],
                configUserInfo: rootState.configUserInfo,
                view: rootState.activeView,
                defaultPath: rootState.defaultPath,
                optionalColumns: rootState.optionalColumns,
            }).pipe(
                // mergeMap(group => pathSelect(
                //     actions.rootSelectPath(group.config.paths.find(e => e.pathIdentifier === group.defaultPath)), deps, group)),
                tap((group) => {
                    if (group.view.pillarsInGrid === PillarsInGrid.ALL) {
                        group.pillars = flattenArrayWithObj(
                            group.view.pillars,
                            'pillars',
                        );
                    } else {
                        group.pillars = [group.root.viewPillar];
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        const parent = group.view.pillars.find(
                            (e) =>
                                e.pillars &&
                                // eslint-disable-next-line @typescript-eslint/prefer-includes
                                e.pillars.indexOf(group.root.viewPillar) !== -1,
                        );
                        if (parent) {
                            group.pillars.splice(0, 0, parent);
                        }
                    }
                }),
                tap((group) => {
                    const supported = flattenSupported(act.viewPillar.metrics);
                    // TODO revise. current actual always enabled
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    group.supportedColumns = group.optionalColumns.filter(
                        (e) =>
                            e.id === OptionalColumnType.CURRENT_ACTUAL ||
                            // eslint-disable-next-line @typescript-eslint/prefer-includes
                            supported.indexOf(e.id) !== -1,
                    );
                    if (group.view.pillarsInGrid === PillarsInGrid.ALL) {
                        group.columns = group.supportedColumns.filter(
                            (e) => e.id === OptionalColumnType.CURRENT_ACTUAL,
                        );
                    } else {
                        group.columns = [...group.supportedColumns];
                    }
                }),
                tap((group) => {
                    const createPair = (
                        i: ConfigViewPillar,
                        e: ConfigViewMetric,
                        path: ConfigViewMetric[],
                    ): MetricPath => {
                        const m = group.config.metrics.find(
                            (ee) => ee.metricId === e.metricId,
                        );
                        return [i, m, path] as MetricPath;
                    };
                    const metricsFlatten = flatten<
                        ConfigViewPillar,
                        MetricPath
                    >(
                        group.view.pillars,
                        (i) => i.metrics.map((e) => createPair(i, e, [e])),
                        (i) => i.pillars,
                    ).filter((e) => e[1]);
                    const metricCompsFlatten = act.viewMetric.components
                        ? act.viewMetric.components
                              .map((e) =>
                                  createPair(act.viewPillar, e, [
                                      act.viewMetric,
                                      e,
                                  ]),
                              )
                              .filter((e) => e[1])
                        : [];
                    if (group.view.pillarsInGrid === PillarsInGrid.ALL) {
                        // const metricsFlatten = flattenMetrics(group.view.pillars);
                        // group.metrics = group.config.metrics.filter(e => metricsFlatten.indexOf(e.metricId) !== -1);
                        group.metrics = metricsFlatten;
                    } else {
                        const metrics = [act.viewMetric.metricId];
                        const metricComponents = /* act.viewMetric.components
                                ? act.viewMetric.components.map(e => e.metricId)
                                : */ []; // TODO revise components auto-selected?
                        // group.metrics = group.config.metrics.filter(e => metrics.indexOf(e.metricId) !== -1);
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        group.metrics = metricsFlatten
                            .filter(
                                (e) =>
                                    act.viewPillar === e[0] &&
                                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                                    metrics.indexOf(e[1].metricId) !== -1,
                            )
                            // eslint-disable-next-line @typescript-eslint/prefer-includes
                            .concat(
                                metricCompsFlatten.filter(
                                    (e) =>
                                        act.viewPillar === e[0] &&
                                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                                        metricComponents.indexOf(
                                            e[1].metricId,
                                        ) !== -1,
                                ),
                            );
                    }
                }),
                map((group) =>
                    actions.rootSelectVisualItemResult(
                        group.root.viewPillar,
                        group.root.viewMetric,
                        group.root.metric,
                        group.root.dataMetric,
                        // group.path,
                        group.pillars,
                        group.supportedColumns,
                        group.columns,
                        group.metrics,
                    ),
                ),
                catchError(() => of(actions.rootSelectVisualItemResult())),
                takeUntil(
                    action.pipe(
                        ofType(
                            actions.ActionType
                                .INSIGHT__ROOT__SELECT_VISUAL_ITEM_CANCEL,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const periodSelectPeriodType = (
    action: ActionsObservable<actions.PeriodSelectPeriodTypeAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT__PERIOD__SELECT_PERIOD_TYPE),
        switchMap((act) => {
            const rootState = pluck<BaseState>(state.value, 'insight');
            const userId =
                rootState.component === ComponentType.HI
                    ? rootState.configUserInfo?.userId
                    : null;

            return of<BaseChainGroup<actions.PeriodSelectPeriodTypeAction>>({
                root: act,
                periodType: rootState.period.periodType,
                cacheDates: rootState.period.cacheDates,
                eventDate: rootState.period.eventDate,
                config: rootState.configs[rootState.activeConfig],
            }).pipe(
                mergeMap((group) =>
                    periodTypeSelect<actions.PeriodSelectPeriodTypeAction>(
                        act,
                        deps,
                        group,
                        userId,
                    ),
                ),
                map((group) =>
                    actions.periodSelectPeriodTypeResult(
                        group.periodType,
                        group.cacheDates,
                        group.eventDate,
                        group.gotoDashboard,
                        group.trendPeriods,
                    ),
                ),
                catchError(() => of(actions.periodSelectPeriodTypeResult())),
                takeUntil(
                    action.pipe(
                        ofType(
                            actions.ActionType
                                .INSIGHT__PERIOD__SELECT_PERIOD_TYPE_CANCEL,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const visualRetrieveData = (
    action: ActionsObservable<actions.VisualRetrieveDataAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT__VISUAL__RETRIEVE_DATA),
        switchMap((act) => {
            const rootState = pluck<BaseState>(state.value, 'insight');
            const pldm = act.parentLevelsDataMap
                ? [...act.parentLevelsDataMap]
                : null;
            const data: DataKey[] = [];
            let dataPg: DataKey[];

            return of<BaseChainGroup<actions.VisualRetrieveDataAction>>({
                root: act,
                config: rootState.configs[rootState.activeConfig],
                view: rootState.activeView,
                path: rootState.activePath,
            }).pipe(
                switchMap((group) => {
                    let obs = dataForLevel(
                        deps,
                        group,
                        act.opts,
                        null,
                        data,
                        0,
                        true,
                    );
                    if (group.view.showPeerGroupVisual) {
                        dataPg = [];
                        obs = obs.pipe(
                            concatMap(() =>
                                dataForLevelPeerGroup(
                                    deps,
                                    group,
                                    act.opts,
                                    pldm,
                                    null,
                                    dataPg,
                                ),
                            ),
                        );
                    }
                    return obs;
                }),
                map(() =>
                    actions.visualRetrieveDataResult(
                        act.opts,
                        act.parentLevelsDataMap,
                        data[0].data[0],
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        dataPg && dataPg[0].data[0],
                    ),
                ),
                catchError(() => of(actions.visualRetrieveDataResult())),
                takeUntil(
                    action.pipe(
                        ofType<actions.VisualRetrieveDataCancelAction>(
                            actions.ActionType
                                .INSIGHT__VISUAL__RETRIEVE_DATA_CANCEL,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const gridRetrieveData = (
    action: ActionsObservable<actions.GridRetrieveDataAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT__GRID__RETRIEVE_DATA),
        mergeMap((act) => {
            const rootState = pluck<BaseState>(state.value, 'insight');
            const pldm = [...act.parentLevelsDataMap];
            const data: DataKey[] = [];
            const dataPg: DataKey[] = [];
            const dataTsg: DataKey[] = [];
            const dataSg: DataKey[] = [];
            const depth = RowId.parse(act.rowId).depth + 1;
            let hierarchyUserInfo: HierarchyUserInfoItem[];
            let hierarchyUserInfoCurrent: HierarchyUserInfoItem[];

            return of<BaseChainGroup<actions.GridRetrieveDataAction>>({
                root: act,
                eventDate: rootState.period.eventDate,
                config: rootState.configs[rootState.activeConfig],
                viewContext: rootState.viewContext,
                path: rootState.activePath,
                supportedColumns: rootState.grid.supportedColumns,
            }).pipe(
                switchMap((group) => {
                    const withPeerGroup = group.supportedColumns.some(
                        (e) =>
                            e.id === OptionalColumnType.PEER_GROUP_ACTUAL ||
                            e.id ===
                                OptionalColumnType.PEER_GROUP_PERCENT_TO_TARGET,
                    );

                    return dataForLevels(
                        deps,
                        group,
                        act.opts.identifier,
                        act.opts,
                        pldm,
                        data,
                        depth,
                        withPeerGroup,
                        dataPg,
                    ).pipe(
                        concatMap(() => {
                            const userIds = userIdsFromData(data);
                            return !userIds || userIds.length === 0
                                ? of(null)
                                : forkJoin(
                                      of({
                                          userIds,
                                          eventDate: group.eventDate,
                                      }).pipe(
                                          listHierarchyUserInfo(deps),
                                          // eslint-disable-next-line no-return-assign
                                          tap(
                                              (ret) =>
                                                  (hierarchyUserInfo =
                                                      ret.result),
                                          ),
                                      ),
                                      of({ userIds }).pipe(
                                          listHierarchyUserInfo(deps),
                                          // eslint-disable-next-line no-return-assign
                                          tap(
                                              (ret) =>
                                                  (hierarchyUserInfoCurrent =
                                                      ret.result),
                                          ),
                                      ),
                                  );
                        }),
                        switchMap((res) => {
                            if (
                                rootState.activeView
                                    .showSuperGroupQueueAssociation !== true
                            ) {
                                return of(res);
                            }
                            return getSuperGroupQueues(
                                deps,
                                group,
                                act.opts.identifier,
                                act.opts,
                                data,
                                res?.[0]?.result,
                                rootState.component,
                            );
                        }),
                    );
                }),
                mergeMap(() =>
                    concat(
                        of(
                            actions.rootCheckPreviousLevelsRecurse(
                                act.opts.identifier,
                            ),
                        ),
                        of(
                            actions.gridRetrieveDataResult(
                                act.opts,
                                act.rowId,
                                act.parentLevelsDataMap,
                                data,
                                dataPg,
                                dataTsg,
                                dataSg,
                                hierarchyUserInfo || [],
                                hierarchyUserInfoCurrent || [],
                            ),
                        ),
                    ),
                ),
                catchError(() => of(actions.gridRetrieveDataResult())),
                takeUntil(
                    action.pipe(
                        ofType<actions.GridRetrieveDataCancelAction>(
                            actions.ActionType
                                .INSIGHT__GRID__RETRIEVE_DATA_CANCEL,
                        ),
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        filter(
                            (cancelAct) =>
                                !cancelAct.rowIds ||
                                // eslint-disable-next-line @typescript-eslint/prefer-includes
                                cancelAct.rowIds.indexOf(act.rowId) !== -1,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const gridExport = (
    action: ActionsObservable<actions.GridExportAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT__GRID__EXPORT),
        mergeMap((act) => {
            const bridge =
                deps.injector.get<InsightEpicBridge>(InsightEpicBridge);
            const translateService =
                deps.injector.get<TranslateService>(TranslateService);
            const exp = new Exporter();

            return of<BaseChainGroup<actions.GridExportAction>>({
                root: act,
            }).pipe(
                mergeMap(() =>
                    from(
                        exp
                            .createWorkbook(
                                bridge.renderer,
                                (l) => {
                                    if (
                                        l.context.r.def.id.startsWith('column')
                                    ) {
                                        return l.context.ctx.name;
                                    }
                                    const columnType = l.context.c.ctx
                                        .type as ColumnType;
                                    switch (columnType) {
                                        case ColumnType.NODE: {
                                            const val =
                                                l.context.ctx.value.name;
                                            if (/^_\d+_.*/.test(val)) {
                                                return translateService.instant(
                                                    val,
                                                );
                                            }
                                            return val;
                                        }
                                        case ColumnType.PEER_GROUP_ACTUAL:
                                        case ColumnType.PEER_GROUP_PERCENT_TO_TARGET:
                                            return valueForColumn(
                                                columnType as any as OptionalColumnType,
                                                l.context.ctx.value.peerGroup,
                                            );
                                        default:
                                            return valueForColumn(
                                                columnType as any as OptionalColumnType,
                                                l.context.ctx.value.metric,
                                            );
                                    }
                                },
                                (c: Column<ColumnContext>) => {
                                    return (
                                        c.ctx.type ===
                                        ColumnType.COMPONENT_METRIC
                                    );
                                },
                            )
                            // eslint-disable-next-line @typescript-eslint/promise-function-async
                            .then(() => exp.serve(act.filename))
                            .then(() => exp.destroy()),
                    ),
                ),
                map(() => actions.gridExportResult(act.filename)),
                catchError(() => {
                    exp.destroy();
                    return of(actions.gridExportResult());
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const trendRetrieveData = (
    action: ActionsObservable<actions.TrendRetrieveDataAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT__TREND__RETRIEVE_DATA),
        mergeMap((act) => {
            const rootState = pluck<BaseState>(state.value, 'insight');
            const data: DataKey[] = [];
            const dataPg: DataKey[] = [];
            const depth = RowId.parse(act.rowId).depth + 1;

            return of<BaseChainGroup<actions.TrendRetrieveDataAction>>({
                root: act,
                config: rootState.configs[rootState.activeConfig],
                view: rootState.activeView,
                path: rootState.activePath,
                supportedColumns: rootState.trend.supportedColumns,
            }).pipe(
                switchMap((group) => {
                    let obs = dataForLevel(
                        deps,
                        group,
                        act.opts,
                        act.parentLevelsDataMap,
                        data,
                        depth,
                        true,
                    );
                    const withPeerGroup = group.supportedColumns.some(
                        (e) =>
                            e.id === OptionalColumnType.PEER_GROUP_ACTUAL ||
                            e.id ===
                                OptionalColumnType.PEER_GROUP_PERCENT_TO_TARGET,
                    );
                    if (withPeerGroup) {
                        obs = obs.pipe(
                            concatMap(() =>
                                dataForLevelPeerGroup(
                                    deps,
                                    group,
                                    act.opts,
                                    act.parentLevelsDataMap,
                                    null,
                                    dataPg,
                                ),
                            ),
                        );
                    }
                    return obs;
                }),
                tap(() => {
                    // if row from grid has data, pad it out if trend doesn't
                    // so that the chart can still have an entry in its legend
                    const rid = RowId.parse(act.rowId);
                    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                    const pp = (dat: DataKey[], pgData?: boolean) => {
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        dat &&
                            dat
                                .filter((e) => !!e.data)
                                .forEach((e) =>
                                    e.data.forEach((ee) => {
                                        if (
                                            ee.id === NO_DATA_ID ||
                                            pgData /* unlike the grid trend rows are flattened. so tie the pg data back to data */
                                        ) {
                                            ee.dataId = rid.dataId;
                                        }
                                    }),
                                );
                    };
                    pp(data);
                    pp(dataPg, true);
                }),
                map(() =>
                    actions.trendRetrieveDataResult(
                        act.opts,
                        act.rowId,
                        act.parentLevelsDataMap,
                        data,
                        dataPg,
                    ),
                ),
                catchError(() => of(actions.trendRetrieveDataResult())),
                takeUntil(
                    action.pipe(
                        ofType<actions.TrendRetrieveDataCancelAction>(
                            actions.ActionType
                                .INSIGHT__TREND__RETRIEVE_DATA_CANCEL,
                        ),
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        filter(
                            (cancelAct) =>
                                !cancelAct.rowIds ||
                                // eslint-disable-next-line @typescript-eslint/prefer-includes
                                cancelAct.rowIds.indexOf(act.rowId) !== -1,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const legendRetrieveData = (
    action: ActionsObservable<actions.LegendRetrieveDataAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT__LEGEND__RETRIEVE_DATA),
        switchMap((act) => {
            const rootState = pluck<BaseState>(state.value, 'insight');

            return of<BaseChainGroup<actions.LegendRetrieveDataAction>>({
                root: act,
                view: rootState.activeView,
            }).pipe(
                switchMap((group) => {
                    const metricIds = Util.recursiveReducer(
                        group.view.pillars,
                        [],
                        {
                            output: (i) => i.metrics.map((m) => m.metricId),
                            input: (i) => i.pillars,
                        },
                    );
                    return forkJoin(
                        of({ metricIds }).pipe(
                            listMetricThresholds(deps),
                            // eslint-disable-next-line no-return-assign
                            tap((ret) => (group.metricThresholds = ret.result)),
                        ),
                        of({ metricIds }).pipe(
                            listTrendsArrows(deps),
                            // eslint-disable-next-line no-return-assign
                            tap((ret) => (group.trendsArrows = ret.result)),
                        ),
                    ).pipe(mapTo(group));
                }),
                map((group) =>
                    actions.legendRetrieveDataResult(
                        group.metricThresholds,
                        group.trendsArrows,
                    ),
                ),
                catchError(() => of(actions.legendRetrieveDataResult())),
                takeUntil(
                    action.pipe(
                        ofType(
                            actions.ActionType
                                .INSIGHT__LEGEND__RETRIEVE_DATA_CANCEL,
                        ),
                    ),
                ),
            );
        }),
    );

function setParents(levels: ConfigPathLevel[], parent: ConfigPathLevel): void {
    if (!levels) return;
    levels.forEach((e) => {
        setParents(e.levels, e);
        (e as any).parent = parent;
    });
}

function unsetParents(levels: ConfigPathLevel[]): void {
    if (!levels) return;
    levels.forEach((e) => {
        unsetParents(e.levels);
        delete (e as any).parent;
    });
}

function stringifyParentReplacer(key: string, value: any): any {
    return key === 'parent' ? null : value;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const rootCheckPreviousLevelsRecurse = (
    action: ActionsObservable<actions.RootCheckPreviousLevelsRecurseAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT__ROOT__CHECK_PREVIOUS_LEVELS_RECURSE),
        mergeMap((act) => {
            const rootState = pluck<BaseState>(state.value, 'insight');

            return of<
                BaseChainGroup<actions.RootCheckPreviousLevelsRecurseAction>
            >({
                root: act,
                config: rootState.configs[rootState.activeConfig],
                path: rootState.activePath,
            }).pipe(
                map((group) => {
                    const level = findLevel(group.path.levels, act.identifier);
                    let nextLevels;
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    if (level && level.levels) {
                        setParents(level.levels, level);
                        nextLevels = level.levels.map((e) => {
                            if (
                                e.previousLevelsRecurse == null ||
                                (e.levels && e.levels.length !== 0)
                            ) {
                                return e;
                            }
                            let ll = e;
                            for (let i = e.previousLevelsRecurse; i > 0; i--) {
                                if (!ll) break;
                                ll = (ll as any).parent;
                            }
                            const newLevel: ConfigPathLevel = JSON.parse(
                                JSON.stringify(e, stringifyParentReplacer),
                            );
                            const nextLevel: ConfigPathLevel = JSON.parse(
                                JSON.stringify(ll, stringifyParentReplacer),
                            );
                            (newLevel as any).parent = level;
                            newLevel.levels = [nextLevel];
                            checkPathLevels(newLevel.levels);
                            return newLevel;
                        });
                        unsetParents(level.levels);
                        unsetParents(nextLevels);
                    }
                    return nextLevels;
                }),
                map((ret) =>
                    actions.rootCheckPreviousLevelsRecurseResult(
                        act.identifier,
                        ret,
                    ),
                ),
                catchError(() => of(null)),
            );
        }),
    );

export default [
    rootListDefinitionSummary,
    rootSelectLevelOption,
    rootRetrieveConfig,
    rootReloadConfig,
    rootSelectVisualItem,
    periodSelectPeriodType,
    visualRetrieveData,
    gridRetrieveData,
    gridExport,
    trendRetrieveData,
    legendRetrieveData,
    rootCheckPreviousLevelsRecurse,
];
